import { useContext, useEffect, useState } from 'react';
import usePagination from 'common/fetchHooks/usePagination';
import { useDebounceCallback } from '@react-hook/debounce';
import MetricsInspectionContext from '../../state/context';
import { ACTIVITY_TYPES } from '../../state/state.utils';
import { getBranchesSnapshots } from 'common/services/branches.service';
import useTableSortBy from './useTableSortBy';
import { mapActivityTypeToEntityKey } from '../../Charts/ScatterChart/useFetchData/entities.utils';
import { getPullRequests } from 'common/services/pull-requests.service';
import { getReleases } from 'common/services/releases.service';
import { metricNames } from '@linearb/linjs-common';
import { METRIC_RAW_EVENTS_DATA_ENRICHMENT } from './utils';

const DEBOUNCE_TIMEOUT = 500;

export const DEFAULT_PAGINATION = {
  PAGE_SIZE: 25,
  PAGES: [10, 25, 50, 100]
};

const mapActivityTypeToFilterKey = {
  [ACTIVITY_TYPES.BRANCHES]: 'branch_ids',
  [ACTIVITY_TYPES.PRS]: 'pull_request_ids',
  [ACTIVITY_TYPES.RELEASES]: 'release_ids'
};

const useActivityData = () => {
  const {
    state: {
      metricName,
      filters: { activityType },
      selectedPoints,
      scatterEntitiesIds,
      isLoadingScatter,
      metricRawEvents
    }
  } = useContext(MetricsInspectionContext);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const pagination = usePagination(DEFAULT_PAGINATION.PAGE_SIZE);
  const tableSortBy = useTableSortBy();

  const isPointSelected = selectedPoints.length > 0;
  const entitiesIds = isPointSelected
    ? selectedPoints
        .map(
          point => point?.extraData?.[mapActivityTypeToEntityKey[activityType]]
        )
        .filter(Boolean)
    : scatterEntitiesIds;

  const ACTIVITY_DATA_API_MAPPER = {
    [ACTIVITY_TYPES.BRANCHES]: getBranchesSnapshots,
    [ACTIVITY_TYPES.PRS]: getPullRequests,
    [ACTIVITY_TYPES.RELEASES]: getReleases
  };

  const fetchData = useDebounceCallback(async () => {
    try {
      const payload = {
        filter: {
          [mapActivityTypeToFilterKey[activityType]]: entitiesIds
        },
        ...(metricName === metricNames.REVIEW_DEPTH
          ? { include_reviews_count: true }
          : {}),
        ...pagination.qs,
        order_by: { [tableSortBy.key]: tableSortBy.dir }
      };

      const res = await ACTIVITY_DATA_API_MAPPER[activityType](payload);
      const { items, total } = res;
      pagination.setTotal(res?.total);
      setData({
        total,
        items: METRIC_RAW_EVENTS_DATA_ENRICHMENT[metricName]
          ? METRIC_RAW_EVENTS_DATA_ENRICHMENT[metricName](
              metricRawEvents,
              items
            )
          : items
      });
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  }, DEBOUNCE_TIMEOUT);

  useEffect(() => {
    if (isLoadingScatter) {
      setIsLoading(true);
      return;
    }
    if (entitiesIds.length === 0) {
      setData({ items: [], total: 0 });
      if (!isLoadingScatter) {
        setIsLoading(false);
      }
      pagination.setTotal(0);
      return;
    }

    setIsLoading(true);
    return fetchData();
  }, [
    pagination.qs.limit,
    pagination.qs.offset,
    tableSortBy.key,
    tableSortBy.dir,
    entitiesIds.join(','),
    isLoadingScatter
  ]);

  return {
    data,
    isLoading,
    pagination,
    tableSortBy
  };
};

export default useActivityData;
